import { Injectable } from '@angular/core';
import { PointImage } from 'app/models/project.model';
import { DemoViewerApiService } from './demo-viewer-api.service';
import { GlobalService } from '../../services/global.service';
import { RefactoredReport } from 'app/models/reports-refactored.model';
import { REPORT_TYPE } from '../project/const';
import { Project360Service } from '../p-dashboard/project360.service';
import { PendingReportFloor } from '../operation-section/pending-reports-models';

@Injectable({
  providedIn: 'root',
})
export class DemoPointsService {
  public pointImages: PointImage[] = [];
  public leftPointImages: PointImage[] = [];
  public defaultPointId: string;
  private defaultReportId: string;
  private assignLeftPointImagesFromRight: boolean = false;

  private rightReportId: string;
  private rightFloorId: string;
  
  private leftReportId: string;
  private leftFloorId: string;
  
  constructor(
    private api: DemoViewerApiService,
    private global: GlobalService,
    private projectService: Project360Service,
  ) {}

  init(defaultPointId: string, defaultReportId: string) {
    this.defaultPointId = defaultPointId;
    this.defaultReportId = defaultReportId;
  }

  /**
   * Get point images for the current floor and report
   * @param floorId - The ID of the floor
   * @param report - The report object
   * @returns Promise<PointImage[]> - Array of point images
   */
  private async getPointImages(floorId: string, report: RefactoredReport): Promise<PointImage[]> {
    const targetReportId = report?.id ?? this.defaultReportId;

    try {
      const response =
        report?.report_type === REPORT_TYPE.VIDEOS
          ? await this.api.getVideoPoints(floorId, targetReportId).toPromise()
          : await this.api.getPointImagesByFloorId(floorId, targetReportId).toPromise();

      return response.data;
    } catch (error) {
      this.global.handleErrors(error);
      return [];
    }
  }
  async updateMainPointImages(floorId: string, report: RefactoredReport) {
    if (floorId !== this.rightFloorId || report.id !== this.rightReportId) {
      this.rightFloorId = floorId;
      this.rightReportId = report.id;
      this.pointImages = await this.getPointImages(floorId, report);
    }
    if (this.assignLeftPointImagesFromRight) {
      this.leftPointImages = this.pointImages;
      this.assignLeftPointImagesFromRight = false;
    }
  }

  // operation engineer section
  updatePendingReportPoints(floor: PendingReportFloor) {
    this.rightFloorId = floor.id;
    this.pointImages = floor.video_points;
  }

  async updateLeftPointImages(floorId: string, report: RefactoredReport) {
    // If same as right report, just reuse the data
    if (floorId == this.rightFloorId && report.id == this.rightReportId) {
      this.leftPointImages = this.pointImages;
      this.assignLeftPointImagesFromRight = true;
      return;
    }
    
    if (floorId === this.leftFloorId && report.id === this.leftReportId && this.leftPointImages?.length > 0) {
      return;
    }
    
    // Fetch new data
    this.leftFloorId = floorId;
    this.leftReportId = report.id;
    this.leftPointImages = await this.getPointImages(floorId, report);
  }

  /**
   * Get image URL for a specific point
   * @param reportId - The ID of the report
   * @param floorId - The ID of the floor
   * @param pointId - The ID of the point
   * @returns Promise<string> - URL of the point image
   */
  async getImageUrlByPointId(reportId: string, floorId: string, pointId: string): Promise<string> {
    const pointImage = await this.projectService.getPImgFrom(reportId, floorId, pointId);
    return pointImage?.image_360 ? pointImage.image_360 : 'assets/webglViewer/space.png';
  }

  /**
   * Set point images for both main and left views
   * @param mainPointImages - Point images for main view
   * @param leftPointImages - Point images for left view
   */
  setPointImages(mainPointImages: PointImage[], leftPointImages: PointImage[]): void {
    this.pointImages = mainPointImages;
    this.leftPointImages = leftPointImages;
  }
  getPointImageNameById(pointImageId: string): string {
    const pointImage = this.pointImages?.find((p) => p.id === pointImageId);
    return pointImage?.point_name;
  }

  getMatchingPointImage(pointImageId: string): PointImage {
    const pointImage = this.pointImages?.find((p) => p.id === pointImageId);
    return pointImage;
  }

  /**
   * Get point image assets with proper handling for operation engineers and blur settings
   * @param source - Point image source data
   * @param organizationSettings - Organization settings containing blur and other toggles
   * @returns Processed assets with imageUrl, thumbUrl, tiles, and tileSourceMode
   */
  public getPointImageAssets(
    source: Partial<PointImage> | null | undefined,
    organizationSettings?: Record<string, unknown>,
  ): {
    imageUrl?: string | null;
    thumbUrl?: string | null;
    tiles?: PointImage['tiles'];
    tileSourceMode: 'standard' | 'blurred';
  } {
    const isOperationEngineer = this.projectService.isOperationEngineer();
    
    if (isOperationEngineer) {
      return {
        imageUrl: source?.image_360 ?? null,
        thumbUrl: undefined,
        tiles: undefined,
        tileSourceMode: 'standard',
      };
    }

    const useBlur = this.isBlurFacesEnabled(organizationSettings);
    let imageUrl = source?.image_360 ?? null;
    let thumbUrl = source?.image_360_thumb ?? null;
    let tiles =
      Array.isArray(source?.tiles) && source.tiles.length > 0 ? source.tiles : undefined;
    let tileSourceMode: 'standard' | 'blurred' = 'standard';

    if (
      useBlur &&
      typeof source?.blurred_image === 'string' &&
      source.blurred_image.trim().length > 0
    ) {
      imageUrl = source.blurred_image;
      if (!thumbUrl || thumbUrl === 'empty') {
        thumbUrl = source.blurred_image;
      }
      tileSourceMode = 'blurred';
    }

    if (
      useBlur &&
      Array.isArray(source?.blurred_tiles) &&
      source.blurred_tiles.length > 0
    ) {
      tiles = source.blurred_tiles;
      tileSourceMode = 'blurred';
    }

    if (!thumbUrl || thumbUrl === 'empty') {
      thumbUrl = imageUrl;
    }

    return {
      imageUrl,
      thumbUrl,
      tiles,
      tileSourceMode,
    };
  }

  /**
   * Check if blur faces is enabled in organization settings
   */
  private isBlurFacesEnabled(organizationSettings?: Record<string, unknown>): boolean {
    if (!organizationSettings) {
      return false;
    }
    const toggleValue = organizationSettings['toggle_blur_faces'];
    return this.normalizeToggleValue(toggleValue);
  }

  /**
   * Normalize toggle value to boolean
   */
  private normalizeToggleValue(value: unknown): boolean {
    if (value === undefined || value === null) {
      return false;
    }
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'string') {
      const lower = value.toLowerCase();
      return lower === '1' || lower === 'true';
    }
    if (typeof value === 'number') {
      return value === 1;
    }
    return false;
  }

  /**
   * Clear all point-related data
   */
  clearPoints(): void {
    this.pointImages = [];
    this.leftPointImages = [];
    this.defaultPointId = null;
    this.defaultReportId = null;
    this.leftFloorId = null;
    this.leftReportId = null;
    this.rightFloorId = null;
    this.rightReportId = null;
  }
}
